/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React, { FC, useMemo, useRef, useState } from 'react';
import { IoMdMove } from 'react-icons/io';

import { Box, Flex } from 'components/styled';
import Text from 'components/styled/Text';

import { useTheme } from 'theme';
import { CrossIcon } from 'assets';

import { getFileUrl } from 'api/fileApi';

import { FileRef } from 'typing/endpoints';

type Props = {
	file: FileRef | File;
	forceHover?: boolean;
	onDragStart?: (dragIndex: number) => void;
	onDragDrop?: (targetIndex: number) => void;
	dragIndex?: number;
	targetIndex?: number;
	onDelete?: () => void;
	sortModeOn?: boolean;
};

const GalleryItem: FC<Props> = ({
	file,
	forceHover,
	onDragStart,
	onDragDrop,
	children,
	dragIndex,
	targetIndex,
	onDelete,
	sortModeOn,
}) => {
	const url = useMemo(() => getFileUrl(file), [file]);
	const ref = useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const [dragOver, setDragOver] = useState(false);
	return (
		<Box
			ref={ref}
			position="relative"
			width="100%"
			pb={`${(3 / 4) * 100}%`}
			css={css`
				border: ${sortModeOn ? 1 : 0}px dashed ${theme.colors.primary};
			`}
			draggable={sortModeOn}
			onDropCapture={() => {
				if (dragIndex !== targetIndex) {
					onDragDrop?.(dragIndex ?? -1);
					if (ref.current) {
						ref.current.style.border = 'none';
					}
				}
			}}
			onDragStart={() => onDragStart?.(dragIndex ?? -1) ?? -1}
			onDragOverCapture={() => {
				if (ref.current) {
					if (
						targetIndex !== dragIndex &&
						targetIndex !== undefined &&
						targetIndex > -1
					) {
						ref.current.style.boxShadow = `inset 10px 0px 0px 0px ${theme.colors.primary}`;

						setDragOver(true);
					}
				}
			}}
			onDragLeaveCapture={() => {
				if (ref.current) {
					ref.current.style.boxShadow = `none`;
					setDragOver(false);
				}
			}}
		>
			<Box
				css={css`
					position: absolute;
					top: 0;
					bottom: 0;
					left: 0;
					right: 0;
					background: url(${url});
					background-position: ${dragOver ? '70%' : ' center'};
					background-size: ${dragOver ? '50%' : 'contain'};
					background-repeat: no-repeat;
					width: ${dragOver ? '120%' : 'unset'};

					&:hover > div,
					&:focus-within > div {
						opacity: 1;
					}
				`}
			>
				<Flex
					position="absolute"
					top={0}
					bottom={0}
					left={0}
					right={0}
					color="white"
					css={css`
						opacity: ${forceHover || dragOver ? '1' : '0'};
						background-color: rgba(0, 0, 0, ${dragOver ? 0.1 : 0.5});
						cursor: pointer;
					`}
				>
					{dragOver ? (
						<>
							<Flex
								flexDirection="column"
								alignItems="center"
								justifyContent="center"
								flex={1}
								opacity="0.8"
								css={css`
									border-left: 8px solid ${theme.colors.primary};
								`}
							></Flex>
						</>
					) : (
						<>
							{(onDelete || sortModeOn) && (
								<>
									<Flex
										flexDirection="column"
										alignItems="center"
										justifyContent="center"
										flex={1}
										onClick={sortModeOn ? undefined : onDelete}
									>
										{sortModeOn ? (
											<IoMdMove size={49} color="white" />
										) : (
											<CrossIcon size={49} color="white" />
										)}
										<Text fontSize="xl" mt={2} mb={0}>
											{sortModeOn ? 'Přetáhněte na novou pozici' : 'Smazat'}
										</Text>
									</Flex>
								</>
							)}
						</>
					)}

					{children}
				</Flex>
			</Box>
		</Box>
	);
};
export default GalleryItem;
